// Editor UI theme: light, dark, or follow the system. The canvas keeps each page's own background.

export type ThemePref = 'system' | 'light' | 'dark';

const KEY = 'vellum.theme';
const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;

export function themePref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    // storage blocked: fall back to the system setting
  }
  return 'system';
}

export function resolvedTheme(pref = themePref()): 'light' | 'dark' {
  return pref === 'system' ? (media?.matches ? 'dark' : 'light') : pref;
}

export function setThemePref(pref: ThemePref) {
  try {
    if (pref === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
  } catch {
    // not persisted; still applies for this session
  }
  applyTheme(pref);
}

const listeners = new Set<() => void>();
export function onThemeChange(fn: () => void) {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

export function applyTheme(pref = themePref()) {
  document.documentElement.dataset.theme = resolvedTheme(pref);
  for (const fn of listeners) fn();
}

media?.addEventListener('change', () => themePref() === 'system' && applyTheme());

/** A theme color for canvas-drawn UI (rulers), read from the CSS variables. */
export function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
